import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { BillingTransaction } from "@/types/billing-transactions";
import {
  computeBillingBalanceStats,
  formatMinorCurrency,
} from "@/utils/billingBalance";

interface TransactionBalanceSummaryProps {
  transactions: BillingTransaction[];
  title?: string;
}

export const TransactionBalanceSummary = ({
  transactions,
  title = "Balance",
}: TransactionBalanceSummaryProps) => {
  const { t } = useTranslation();

  const stats = useMemo(
    () => computeBillingBalanceStats(transactions),
    [transactions],
  );

  return (
    <div className="w-full mb-6">
      <h3 className="text-[28px] leading-[32px] font-[400] text-[#0F0F0F] mb-6">
        {title}
      </h3>

      <div className="grid grid-cols-3 text-sm text-[#6B7280] mb-4 px-2">
        <div>{t("billing.debet", "Debet")}</div>
        <div className="text-center">{t("billing.credit", "Credit")}</div>
        <div className="text-right">{t("billing.balance", "Balance")}</div>
      </div>

      <div className="grid grid-cols-3 items-end mb-8 px-2">
        <div className="text-md leading-[30px] text-[#EF4444]">
          {formatMinorCurrency(stats.debetMinor, stats.currency)}
        </div>

        <div className="text-center text-md leading-[30px] text-[#22C55E]">
          {formatMinorCurrency(stats.creditMinor, stats.currency)}
        </div>

        <div
          className={`text-right text-md leading-[30px] ${
            stats.balanceMinor < 0 ? "text-[#EF4444]" : "text-[#22C55E]"
          }`}
        >
          {formatMinorCurrency(stats.balanceMinor, stats.currency)}
        </div>
      </div>

      <div className="border-[1px] border-[#E5E7EB]" />
    </div>
  );
};
